import { useMutation } from '@apollo/client/react';
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { removeFromCache } from '@graphql/cache-helper';
import { RemoveCardDocument } from '@graphql/mutations/cards/remove-card.graphql';
import useWorkspaceUrl from '@hooks/use-workspace-url';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Heading,
  Inline,
  Modal,
  Stack,
  Text,
} from '@ui-lib';
import { useRouter } from 'next/router';

import * as styles from './DeleteCardWarningDialog.css';

type DeleteCardWarningDialogProps = {
  cardId: string | undefined;
  show: boolean;
  route: 'personal' | 'team' | 'templates';
  cardsAffected?: number;
  close: () => void;
};

export function DeleteCardWarningDialog({
  cardId,
  show,
  route,
  cardsAffected,
  close,
}: DeleteCardWarningDialogProps): JSX.Element {
  const [removeCard, { loading }] = useMutation(RemoveCardDocument);

  const router = useRouter();
  const previewCardUrl = useWorkspaceUrl(`cards/${route}/`);

  async function handleDelete() {
    if (!cardId) {
      close();
      return;
    }
    await removeCard({
      variables: { input: { id: cardId } },
      update: removeFromCache({ id: cardId, __typename: 'Card' }),
    });

    await router.replace(previewCardUrl);

    close();
  }

  const cardsAffectedMessage = cardsAffected === 1 ? 'is 1 card' : `are ${cardsAffected} cards`;

  return (
    <Modal open={show} handleClose={close}>
      <Dialog>
        <DialogContent>
          <Box margin="small">
            <Stack gap="medium">
              <Box className={styles.NotificationIcon} display="flex" justifyContent="center">
                <FontAwesomeIcon icon={faExclamationCircle} />
              </Box>
              <Heading level="6">Are you sure?</Heading>

              <Text className={styles.textStyle}>
                {route === 'templates'
                  ? `This action cannot be undone, if you delete this template it is gone forever. Any cards connected to
                  this template will be unlinked from this template but not deleted. There ${cardsAffectedMessage} linked to this template.`
                  : `This action cannot be undone, if you delete this card it is gone forever.`}
              </Text>
            </Stack>
          </Box>
        </DialogContent>
        <DialogFooter>
          <Inline align="right" gap="small">
            <Button variant="text" onClick={close}>
              Cancel
            </Button>
            <Button variant="solid" onClick={handleDelete} loading={loading}>
              Delete {route === 'templates' ? 'Template' : 'Card'}
            </Button>
          </Inline>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
